import { CheckSquare, Square } from 'lucide-react';
import {
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styles from './AutocompleteInput.module.scss';
import ChipStyles from './ChipStyles';

import {
  Autocomplete,
  Checkbox,
  createFilterOptions,
  FormHelperText,
  TextField
} from '@mui/material';

const icon = <Square size={20} />;
const checkedIcon = <CheckSquare size={20} />;
const filterByAllKeys = (arr1, arr2) => {
  // We want all elements from arr1 that don't exist in arr2
  // Comapring key by key
  const allKeys = Object.keys(arr1[0]);
  const result = [];
  for (let i = 0; i < arr1.length; i++) {
    const arr1Element = arr1[i];
    let arr1Element1ExistInArray2 = false;
    for (let j = 0; j < arr2.length; j++) {
      const arr2Element = arr2[j];
      // Compares both
      let isSame = true;
      for (let k = 0; k < allKeys.length; k++) {
        const key = allKeys[k];
        if (arr1Element[key] !== arr2Element[key]) isSame = false;
      }
      if (isSame) arr1Element1ExistInArray2 = true;
    }
    if (!arr1Element1ExistInArray2) result.push(arr1Element);
  }
  return result;
};

const OptionsGroupButtonUnselect = ({ groupData, value, onValueChanges }) => {
  return (
    <Button
      text={'Unselect all'}
      variant={'secondary'}
      rounded={'small'}
      size={'xsmall'}
      onClick={() => onValueChanges(filterByAllKeys(value, groupData))}
    />
  );
};

const OptionsGroupButtonSelect = ({ groupData, value, onValueChanges }) => {
  return (
    <Button
      text={'Select all'}
      variant={'primaryLight'}
      rounded={'small'}
      size={'xsmall'}
      onClick={() => {
        const unique = [...value, ...groupData].filter(
          (value, index, self) =>
            index ===
            self.findIndex(t => t.name === value.name && t.npn === value.npn)
        );
        onValueChanges(unique);
      }}
    />
  );
};

const AutocompleteInput = ({
  options = [],
  value = '' | [],
  label = 'Select value',
  placeholder = 'Search',
  multiple = false,
  freeSolo = false,
  onValueChanges,
  disableSearch = false,
  loading = false,
  loadingBlocksOptions = false,
  selectAll = false,
  helperText,
  getOptionLabel = option => option,
  isOptionEqualToValue = (option, value) => option === value,
  insideButton,
  timesInputClosed,
  selectAllByGroup = false,
  showScrollbar = false,
  listBoxProps = {},
  getOptionSecondaryLabel,
  hideFloatingLabel = false,
  ...others
}) => {
  // Close stuff
  const autocompleteReff = React.useRef(null);
  const [open, setOpen] = useState(false);

  // After generate table
  useEffect(() => {
    if (timesInputClosed > 0) {
      setOpen(false);
    }
  }, [timesInputClosed]);

  const allOptionsAreSelected = options.length === value?.length;
  const filter = createFilterOptions();

  const handleChangeWithAll = (_, selectedOptions, reason) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (selectedOptions.find(option => option.type === 'Select All')) {
        if (allOptionsAreSelected) {
          return onValueChanges([]);
        } else {
          let result = [];
          result = options.filter(el => el.type !== 'Select All');
          return onValueChanges(result);
        }
      } else {
        return onValueChanges(selectedOptions);
      }
    } else if (reason === 'clear') {
      return onValueChanges([]);
    }
  };

  const handleChange = (_, selectedOptions) => {
    if (onValueChanges) onValueChanges(selectedOptions);
  };

  const handleChangeSelect = event => {
    if (onValueChanges) onValueChanges(event);
  };

  const resolvedSelectBaseId =
    typeof others?.id === 'string' && others.id
      ? others.id
      : 'mui-autocomplete-simple-select';
  const selectFloatingLabelDomId = `${resolvedSelectBaseId}-label`;
  const showFloatingSelectLabel =
    disableSearch && !hideFloatingLabel && !!(label && String(label).trim());
  const showPlaceholderAsEmptyValue =
    disableSearch &&
    hideFloatingLabel &&
    !!(placeholder && String(placeholder).trim());

  const handleRenderGroup = ({ group, children }) => {
    return (
      <div key={group}>
        <div
          style={{
            height: group ? 60 : 0,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              marginLeft: 10,
              marginRight: 10,
              color: '#888',
              fontSize: 14
            }}
          >
            {group}
          </div>
          {selectAllByGroup && group !== undefined && group !== 'Main Agent' ? (
            <div style={{ display: 'flex', gap: '10px' }}>
              <OptionsGroupButtonSelect
                groupData={children.map(c => JSON.parse(c.key))}
                value={value}
                onValueChanges={onValueChanges}
              />
              <OptionsGroupButtonUnselect
                groupData={children.map(c => JSON.parse(c.key))}
                value={value}
                onValueChanges={onValueChanges}
              />
            </div>
          ) : null}
        </div>
        <div>
          {children.map((child, index) => (
            <div key={index}>{child}</div>
          ))}
        </div>
      </div>
    );
  };

  const handleRenderOption = (props, option, { selected }) => {
    const selectAllProps =
      option.type === 'Select All' // To control the state of 'select-all' checkbox
        ? { checked: allOptionsAreSelected }
        : {};
    return (
      <li
        {...props}
        key={JSON.stringify(option)}
        style={{
          pointerEvents: loading && loadingBlocksOptions ? 'none' : 'auto'
        }}
      >
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selected}
          {...selectAllProps}
        />
        {typeof option === 'object' ? option.name : option}
      </li>
    );
  };

  const renderInput = params => {
    return (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color='inherit' size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          )
        }}
      />
    );
  };

  return (
    <div
      className={`${styles.autocomplete} ${
        showScrollbar && styles.showScrollbar
      }`}
    >
      {disableSearch ? (
        <FormControl sx={{ width: '100%' }}>
          {showFloatingSelectLabel ? (
            <InputLabel id={selectFloatingLabelDomId} shrink={Boolean(value)}>
              {label}
            </InputLabel>
          ) : null}
          <Select
            {...others}
            labelId={
              showFloatingSelectLabel
                ? selectFloatingLabelDomId
                : others.labelId
            }
            id={resolvedSelectBaseId}
            value={value}
            label={showFloatingSelectLabel ? label : undefined}
            placeholder={placeholder}
            onChange={handleChangeSelect}
            SelectDisplayProps={{
              style: {
                height: '1.4375em'
              }
            }}
            displayEmpty={
              showPlaceholderAsEmptyValue || Boolean(others.displayEmpty)
            }
            {...(showPlaceholderAsEmptyValue
              ? {
                  renderValue: selected =>
                    selected != null && selected !== '' ? (
                      selected
                    ) : (
                      <span className='text-sm text-[var(--color-grey)]'>
                        {placeholder}
                      </span>
                    )
                }
              : {})}
          >
            {options?.map(raw => {
              const option =
                typeof raw === 'string' ? raw : getOptionLabel(raw);
              const itemKey =
                typeof raw === 'object' &&
                raw != null &&
                raw.value !== undefined &&
                raw.value !== null
                  ? String(raw.value)
                  : option;
              const secondary =
                typeof getOptionSecondaryLabel === 'function' &&
                typeof raw === 'object' &&
                raw != null
                  ? getOptionSecondaryLabel(raw)
                  : null;

              return (
                <MenuItem key={itemKey} value={option}>
                  {secondary ? (
                    <div className='flex min-w-0 flex-col gap-0.5 py-0.5 text-left'>
                      <span className='text-sm leading-snug'>{option}</span>
                      <span className='text-xs font-normal leading-snug text-[var(--color-grey)]'>
                        {secondary}
                      </span>
                    </div>
                  ) : (
                    option
                  )}
                </MenuItem>
              );
            })}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      ) : (
        <Autocomplete
          ref={autocompleteReff}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          loading={loading}
          loadingText={'Loading...'}
          multiple={multiple}
          freeSolo={freeSolo}
          value={value}
          id='autocompletetags'
          options={options}
          disableCloseOnSelect
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          renderOption={handleRenderOption}
          style={{
            width: '100%'
          }}
          ListboxProps={listBoxProps}
          disablePortal
          renderInput={renderInput}
          renderTags={(value, getTagProps) => {
            if (value?.length === 0) return null;

            return (
              <>
                <div
                  style={{
                    maxHeight: '150px',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    overflowY: 'auto'
                  }}
                >
                  {value.slice(0, 150).map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex'
                      }}
                    >
                      {index === 0 ? insideButton : <></>}
                      <Chip
                        size='medium'
                        sx={ChipStyles}
                        label={
                          typeof option === 'object' ? option.name : option
                        }
                        {...getTagProps({ index })}
                      />
                    </div>
                  ))}

                  {value?.length > 150 && (
                    <p
                      style={{
                        margin: 0,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                      }}
                    >{`+ ${value?.length - 150} more...`}</p>
                  )}
                </div>
              </>
            );
          }}
          renderGroup={handleRenderGroup}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (selectAll)
              return [{ name: 'Select All', type: 'Select All' }, ...filtered];
            else return filtered;
          }}
          onChange={selectAll ? handleChangeWithAll : handleChange}
          open={open}
          {...others}
        />
      )}
    </div>
  );
};

AutocompleteInput.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  freeSolo: PropTypes.bool,
  onValueChanges: PropTypes.func,
  disableSearch: PropTypes.bool,
  loading: PropTypes.bool,
  loadingBlocksOptions: PropTypes.bool,
  selectAll: PropTypes.bool,
  helperText: PropTypes.string,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  insideButton: PropTypes.any,
  timesInputClosed: PropTypes.number,
  selectAllByGroup: PropTypes.bool,
  showScrollbar: PropTypes.bool,
  listBoxProps: PropTypes.object,
  getOptionSecondaryLabel: PropTypes.func,
  hideFloatingLabel: PropTypes.bool
};

OptionsGroupButtonSelect.propTypes = {
  groupData: PropTypes.array.isRequired,
  value: PropTypes.any,
  onValueChanges: PropTypes.func
};

OptionsGroupButtonUnselect.propTypes = {
  groupData: PropTypes.array.isRequired,
  value: PropTypes.any,
  onValueChanges: PropTypes.func
};

export default AutocompleteInput;
